import React, { forwardRef } from "react";
import clsx from "clsx";
import { FlagDivider, Section, Text } from "@/components";
import { useApp } from "@/hooks";
import "./PricingSection.scss"; // Import the styles


interface LpShareSectionProps {
  className?: string;
}

const LpShareSection = ({ className = "my-6" }: LpShareSectionProps) => {
  return (
    <div className={`flex flex-col items-center justify-center gap-4 text-white sm:mt-12 sm:flex-row sm:items-center sm:gap-12 ${className}`}>
      <div className="text-white text-[18px] leading-[20px] md:text-[28px] md:leading-[20px] font-inter">
        Our Partner
      </div>
      <div className="flex items-center gap-4">
        <a href="https://www.lpshares.com" target="_blank">
          <img
            src="/images/lpshares/lp-logo.png"
            alt="LP Shares"
            className="w-[251px] h-auto object-contain"
          />
        </a>
      </div>
    </div>
  )
}

export const PricingSection = forwardRef<HTMLElement, {}>((props, ref) => {
  const { doGetStarted } = useApp();
  // Pricing plan data
  const pricingPlans = [
    {
      price: "$200",
      period: "month",
      plan: "Essential",
      planDescription: "Great for new investors",
      features: [
        {
          "name": "1 deal/month",
          "description": "Each company or data room you upload counts as one deal, regardless of how many documents it contains. Monthly allotment resets; unused deals do not carry forward"
        },
        {
          "name": "1 seat",
          "description": "Each seat = one team member. All users share a single source of truth—seeing the same flags, questions, and answers in real time."
        },
        {
          "name": "PDF, Word, Excel",
          "description": "File types supported by our AI"
        },
        // {
        //   "name": "50 DD credits/deal",
        //   "description": "DD (Due Diligence) credits equate to AI processing power. One credits is roughly equal to one PDF page or one XLS table."
        // }
      ],
      default: false,
    },
    {
      price: "$1,200",
      period: "month",
      plan: "Professional",
      planDescription: "For mid-sized teams that want to pay monthly",
      features: [
        {
          "name": "50 deals/month",
          "description": "Each company or data room you upload counts as one deal, regardless of how many documents it contains. Monthly allotment resets; unused deals do not carry forward"
        },
        {
          "name": "3 seats",
          "description": "Each seat = one team member. All users share a single source of truth—seeing the same flags, questions, and answers in real time."
        },
        {
          "name": "PDF, Word, Excel",
          "description": "File types supported by our AI"
        },
        // {
        //   "name": "100 DD credits/deal",
        //   "description": "DD (Due Diligence) credits equate to AI processing power. One credits is roughly equal to one PDF page or one XLS table."
        // },
        {
          "name": "Data room integrations",
          "description": "Current support for Docsend & Google Drive for seamless document import. Support for One Drive, Box, and DropBox will be coming soon."
        }
      ],
      default: true,
    },
    {
      price: "$12,000",
      period: "year",
      plan: "Premier",
      planDescription: "For high-volume teams who want the best value",
      features: [
        {
          "name": "75 deals/month",
          "description": "Each company or data room you upload counts as one deal, regardless of how many documents it contains. Monthly allotment resets; unused deals do not carry forward"
        },
        {
          "name": "7 seats",
          "description": "Each seat = one team member. All users share a single source of truth—seeing the same flags, questions, and answers in real time."
        },
        {
          "name": "PDF, Word, Excel",
          "description": "File types supported by our AI"
        },
        // {
        //   "name": "200 DD credits/deal",
        //   "description": "DD (Due Diligence) credits equate to AI processing power. One credits is roughly equal to one PDF page or one XLS table."
        // },
        {
          "name": "Data room integrations",
          "description": "Current support for Docsend & Google Drive for seamless document import. Support for One Drive, Box, and DropBox will be coming soon."
        }
      ],
      default: false,
    },
  ];

  // Enterprise features
  const enterpriseFeatures = [
    ["100+ Deals/Months", "White Glove On Premise Deployment", "Custom Prompt Training"],
    ["10+ Seats", "Custom Integrations", "PDF, XLS, Docsend"],
  ];

  return (
    <Section
      ref={ref}
      id="pricing-section"
      className="flex-col mx-0 px-4 py-10 lg:py-[84px] mt-[20px] md:mt-[0px] rounded-t-[20px] md:rounded-t-[60px] bg-white"
    >
      <div className="w-full lg:w-[856px] mx-auto pt-0">
        <div className="flex flex-col items-center gap-y-6 md:gap-y-10 w-full">
          <div className="flex flex-col items-center">
            <div className="hidden md:flex flex gap-2">
              <Text.Heading size="lg" className="font-semibold text-black text-center md:leading-[78px]">
                Simple, Scalable
              </Text.Heading>
              <Text.Heading size="lg" className="font-semibold text-black text-center md:leading-[78px] pricing-title">
                Pricing
              </Text.Heading>
            </div>

            <div className="flex flex-col items-center md:hidden gap-1">
              <div className="flex gap-1.5">
                <Text.Heading size="lg" className="font-400 font-calSans text-black text-center pricing-section-title">
                  Simple, Scalable
                </Text.Heading>
                <Text.Heading size="lg" className="font-400 font-calSans text-black text-center pricing-section-title pricing-title">
                  Pricing
                </Text.Heading>
              </div>

              <Text.Description size="md" className="max-w-[283px] !text-[#383838] text-center">
                Instant answers from your entire data room — no searching, no sifting. Just ask.
              </Text.Description>
            </div>
          </div>

          <div className="w-full px-4 sm:px-0 max-w-[376px] w-[528px] lg:max-w-[838px] lg:w-[838px] flex flex-col gap-y-5">
            <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
              {pricingPlans.map((plan, index) => (
                <div
                  key={`plan-${index}`}
                  className="pricing-option-wrapper"
                >
                  <div className="pricing-option flex flex-col h-full px-8 py-10 md:px-6 md:py-[30px]">
                    <div className="flex flex-col">
                      <div className="flex flex-col gap-[14px] md:gap-[10px] mb-14 md:mb-10">
                        <Text.Description
                          size="md"
                          className="text-black !font-semibold !text-[28px] md:!text-[20px] md:!leading-[20px]"
                        >
                          {plan.plan}
                        </Text.Description>

                        <Text.Description
                          size="2xs"
                          className="text-black !text-[20px] md:!text-[14px] md:!leading-[20px] min-h-[40px]"
                        >
                          {plan.planDescription}
                        </Text.Description>
                      </div>

                      <div className="flex flex-col mb-10 md:mb-7">
                        <div className="flex items-end gap-[3px]">
                          <span className="font-semibold text-black text-[42px] leading-[48px] md:text-[30px] md:leading-[34px] tracking-[-2.5%]">
                            {plan.price} /{" "}
                          </span>
                          <span className="font-medium text-[#789C9C] text-[20px] md:text-[14px] md:leading-[20px] tracking-[-2.5%]">
                            {plan.period}
                          </span>
                        </div>

                        <div className="flex gap-1 mt-5">
                          <Text.Description
                            size="2xs"
                            className="text-black !font-bold !text-[20px] md:!text-[14px] md:leading-[20px]"
                          >
                            14 day free trial
                          </Text.Description>

                          <Text.Description
                            size="2xs"
                            className="!text-[#142E2F] !text-[20px] md:!text-[14px] md:leading-[20px]"
                          >
                            included
                          </Text.Description> 
                        </div>
                      </div>

                      <div className={`pricing-option-btn-wrapper ${plan.default && 'default'}`}>
                        <div
                          className={`pricing-option-btn w-full h-9 flex items-center justify-between px-4 py-2.5 rounded-md bg-white`}
                          onClick={doGetStarted}
                        >
                          <span className="mx-auto font-inter font-medium text-sm text-[#17181D] tracking-[0.21px]">
                            Get Started
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="my-[22px] md:my-[14px]">
                      <FlagDivider type="green" />
                    </div>

                    <div className="flex flex-col items-start justify-center gap-1 mt-2">
                      <Text.Description
                        size="2xs"
                        className="!text-[#243A3A] mb-1 !text-[20px] md:!text-[14px] md:!leading-[28px]"
                      >
                        What's included:
                      </Text.Description>
                      {plan.features.map((feature, featureIndex) => (
                        <div
                          key={`feature-${featureIndex}`}
                          className="pricing-feature flex items-center gap-2 w-full"
                        >
                          <div className="flex items-center gap-1">
                            <img
                              src="/images/home/tick.svg"
                              alt="Check icon"
                              className="w-5 h-5"
                              loading="lazy"
                            />
                            <span className="text-black text-[20px] md:text-[14px] md:leading-[28px] leading-7 font-500">
                              {feature.name}
                            </span>
                          </div>

                          {feature.description && (
                            <button
                              type="button"
                              className="pricing-feature__tooltip"
                              aria-label={`More information about ${feature.name}`}
                            >
                              <img
                                src="/images/home/info.svg"
                                alt=""
                                aria-hidden="true"
                                className="w-4 h-4"
                                loading="lazy"
                              />
                              <span className="pricing-feature__tooltip-content">
                                {feature.description}
                              </span>
                            </button>
                          )}
                        </div>
                      ))}

                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* enterprise plan for desktop */}
            <div className="hidden md:block w-full enterprise-section p-5 md:p-2.5 rounded-[20px]">
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-3 w-full">
                  <div>
                    <Text.Heading
                      size="sm"
                      className="enterprise-plan-title font-semibold md:!text-[44px] md:leading-[55px] text-center md:mt-5"
                    >
                      Enterprise Plan
                    </Text.Heading>
                  </div>

                  <div className="schedule-btn-wrapper mb-2">
                    <div
                      className="w-[300px] p-2 schedule-btn cursor-pointer rounded-md text-center text-[14px] font-medium text-[#17181D]"
                      onClick={() => {
                        window.open('https://calendly.com/dealroom-sales/30min', '_blank');
                      }}
                    >
                      Schedule a call
                    </div>
                  </div>

                  <div className="w-full bg-white mt-6 rounded-[14px]">
                    <div className="hidden lg:flex justify-center py-10 px-[97.5px]">
                      {enterpriseFeatures.map((row, rowIndex) => (
                        <div
                          key={`row-${rowIndex}`}
                          className={clsx(
                            "flex flex-col flex-1 gap-3",
                            {
                              "border-r border-solid border-[#E5E7EB] pr-8": rowIndex === 0,
                              "pl-8": rowIndex !== 0,
                            }
                          )}
                        >
                          {row.map((feature, featureIndex) => (
                            <div
                              key={`enterprise-feature-${featureIndex}`}
                              className="flex items-center gap-1"
                            >
                              <img
                                src="/images/home/tick.svg"
                                alt="Check icon"
                                className="w-5 h-5"
                                loading="lazy"
                              />
                              <span className="font-medium text-[#1A1A1A] text-[14px] font-inter font-medium leading-[20px] tracking-[1.5%]">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    <div className="block lg:hidden grid gird-cols-1 px-[14px] py-[46px] gap-6">
                      {enterpriseFeatures.flatMap((row, rowIndex) => (
                        <div
                          key={`row-${rowIndex}`}
                          className="flex flex-col flex-1 gap-6"
                        >
                          {row.map((feature, featureIndex) => (
                            <div
                              key={`enterprise-feature-${featureIndex}`}
                              className="flex items-center gap-1"
                            >
                              <img
                                src="/images/home/tick.svg"
                                alt="Check icon"
                                className="w-5 h-5"
                                loading="lazy"
                              />
                              <span className="font-medium text-[#1A1A1A] text-[16px] font-inter font-medium leading-[23.14px]">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Lpshares partner */}
              <LpShareSection />
            </div>
          </div>

          {/* enterprise plan for mobile */}
          <div className="block md:hidden w-full enterprise-section p-[14px] rounded-[24px]">
            <div className="w-full flex flex-col items-center gap-4">
              <div className="w-full flex flex-col items-center gap-3">
                <div className="mt-2">
                  <Text.Heading
                    size="sm"
                    className="enterprise-plan-title font-semibold text-[40px] leading-[100%] text-center"
                  >
                    Enterprise Plan
                  </Text.Heading>
                </div>

                <div className="schedule-btn-wrapper">
                  <div
                    className="w-[300px] p-2 schedule-btn cursor-pointer rounded-md text-center text-[14px] font-medium text-[#17181D]"
                    onClick={() => {
                      window.open("https://calendly.com/dustinrbyington/30min", "_blank");
                    }}
                  >
                    Schedule a call
                  </div>
                </div>

                <div className="w-full bg-white mt-[10px] rounded-[14px]">
                  <div className="grid gird-cols-1 px-[14px] py-[46px] gap-6">
                    {enterpriseFeatures.flatMap((row, rowIndex) => (
                      <div
                        key={`row-${rowIndex}`}
                        className="flex flex-col flex-1 gap-6"
                      >
                        {row.map((feature, featureIndex) => (
                          <div
                            key={`enterprise-feature-${featureIndex}`}
                            className="flex items-center gap-1"
                          >
                            <img
                              src="/images/home/tick.svg"
                              alt="Check icon"
                              className="w-5 h-5"
                              loading="lazy"
                            />
                            <span className="font-medium text-[#1A1A1A] text-[16px] font-inter font-medium leading-[23.14px]">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lpshares partner */}
              <LpShareSection />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
});
